// Rongta RP820 (80mm, 203dpi, ESC/POS) thermal printing from the browser via the Web Serial API.
// The RP820 exposes a USB/Serial COM port; we stream raw ESC/POS bytes to it.
// Requires a secure context (HTTPS/localhost) and a Chromium browser (Chrome/Edge).
const BAUD_KEY = "dw_rp820_baud";
const WIDTH = 48; // Font A on 72mm print width = 48 columns

export const isSerialSupported = () => typeof navigator !== "undefined" && "serial" in navigator;
export const getBaud = () => parseInt(localStorage.getItem(BAUD_KEY) || "9600", 10) || 9600;
export const setBaud = (b) => localStorage.setItem(BAUD_KEY, String(parseInt(b, 10) || 9600));

const fmt = (n) => `$${(Number(n) || 0).toFixed(2)}`;

// RP820 prints single-byte code pages; map common unicode to ASCII fallbacks, drop the rest.
const FALLBACK = { "·": "-", "—": "-", "–": "-", "’": "'", "‘": "'", "“": '"', "”": '"', "…": "...", "×": "x" };
const encodeLatin = (s) => {
  const out = [];
  for (const ch of String(s ?? "")) {
    const mapped = FALLBACK[ch] ?? ch;
    for (const c of mapped) { const code = c.charCodeAt(0); out.push(code < 256 ? code : 0x3f); }
  }
  return out;
};

class EscPos {
  constructor() { this.bytes = []; }
  raw(arr) { this.bytes.push(...arr); return this; }
  init() { return this.raw([0x1b, 0x40]); }
  align(a) { return this.raw([0x1b, 0x61, a === "center" ? 1 : a === "right" ? 2 : 0]); }
  bold(on) { return this.raw([0x1b, 0x45, on ? 1 : 0]); }
  size(dbl) { return this.raw([0x1d, 0x21, dbl ? 0x11 : 0x00]); }
  text(s) { return this.raw(encodeLatin(s)); }
  line(s = "") { return this.text(s).raw([0x0a]); }
  feed(n = 1) { return this.raw([0x1b, 0x64, n]); }
  divider(ch = "-") { return this.line(ch.repeat(WIDTH)); }
  row(l, r) {
    const left = String(l ?? ""), right = String(r ?? "");
    const gap = Math.max(1, WIDTH - left.length - right.length);
    return this.line(left + " ".repeat(gap) + right);
  }
  cut() { return this.raw([0x1d, 0x56, 65, 3]); } // feed 3 lines + full cut
  drawerKick() { return this.raw([0x1b, 0x70, 0x00, 0x19, 0xfa]); }
  encode() { return new Uint8Array(this.bytes); }
}

export function buildReceiptBytes(sale, s = {}) {
  const p = new EscPos().init();
  p.align("center").bold(true).size(true).line((s.store_name || "DEALSWAGON OUTLET").toUpperCase()).size(false).bold(false);
  (s.store_address_block || "").split("\n").map((l) => l.trim()).filter(Boolean).forEach((ln) => p.line(ln));
  p.align("left").divider();
  p.row("Invoice", sale.invoice_number);
  const d = sale.created_at ? new Date(sale.created_at) : new Date();
  p.row("Date", `${d.toLocaleDateString("en-CA")} ${d.toLocaleTimeString("en-CA", { hour: "2-digit", minute: "2-digit" })}`);
  p.row("Cashier", sale.cashier_name || "");
  p.divider();
  (sale.items || []).forEach((l) => {
    p.bold(true).line(l.product_name || "").bold(false);
    const meta = [l.sku, l.size && `Sz ${l.size}`, l.colour].filter(Boolean).join(" ");
    if (meta) p.line(meta);
    p.row(`  ${l.quantity} x ${fmt(l.unit_price)}${l.discount ? " -" + fmt(l.discount) : ""}`, fmt(l.line_total));
  });
  p.divider();
  p.row("Subtotal", fmt(sale.subtotal));
  if (sale.discount_total) p.row("Cart discount", "-" + fmt(sale.discount_total));
  p.row(`HST (${sale.hst_rate}%)`, fmt(sale.hst_amount));
  p.bold(true).row("TOTAL", `${fmt(sale.total)} ${s.currency || "CAD"}`).bold(false);
  p.row("Payment", sale.payment_method || "");
  if (sale.payment_method === "Cash") { p.row("Cash", fmt(sale.cash_received)); p.row("Change", fmt(sale.change_due)); }
  p.divider();
  p.align("center").line(s.invoice_footer || "Thank you for shopping with DealsWagon Outlet.");
  p.line(s.return_policy || "All sales are final.");
  p.feed(1).cut();
  return p.encode();
}

let cachedPort = null;

async function acquirePort(forcePicker) {
  if (!isSerialSupported()) throw new Error("Serial printing needs Chrome or Edge over HTTPS.");
  if (!forcePicker) {
    if (cachedPort) return cachedPort;
    const granted = await navigator.serial.getPorts();
    if (granted.length) { cachedPort = granted[0]; return cachedPort; }
  }
  cachedPort = await navigator.serial.requestPort(); // must run inside a user gesture
  return cachedPort;
}

async function writeToPort(port, bytes) {
  const wasOpen = !!port.readable;
  if (!wasOpen) await port.open({ baudRate: getBaud() });
  const writer = port.writable.getWriter();
  try {
    await writer.write(bytes);
  } finally {
    writer.releaseLock();
    if (!wasOpen) { try { await port.close(); } catch (e) { console.warn("RP820 port close failed (already closing)", e); } }
  }
}

// Print a sale receipt to the RP820. First call prompts a one-time COM-port pick.
export async function printReceiptRP820(sale, settings) {
  const port = await acquirePort(false);
  await writeToPort(port, buildReceiptBytes(sale, settings));
}

// Let the cashier (re)select the printer and validate the chosen baud rate.
export async function connectRP820() {
  const port = await acquirePort(true);
  if (!port.readable) { await port.open({ baudRate: getBaud() }); await port.close(); }
  return true;
}
